import PackageIcon from "@redotech/redo-icons/package.svg";
import { isCoverageFeeProduct } from "@redotech/redo-return-coverage-common/is-coverage-fee-product";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoOrderSummary } from "@redotech/redo-web/arbiter-components/order-summary/redo-order-summary";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext } from "react";
import { NotFound } from "../../common-components/not-found";
import { ShowMoreLessButton } from "../../common-components/show-more-less-button";
import { OrdersContext } from "../../contexts/orders";
import { WidgetView } from "../../core/routes";
import { useShowMoreLess } from "../../hooks/use-show-more-less";
import { useWidgetNavigation } from "../../hooks/use-widget-navigation";
import * as styles from "./recent-orders-section.module.css";

interface RecentOrdersSectionProps {
  headerText?: string;
}

export const RecentOrdersSection = memo(function RecentOrdersSection({
  headerText = "Recent orders",
}: RecentOrdersSectionProps) {
  const ordersContext = useContext(OrdersContext);
  const { navigate } = useWidgetNavigation();

  // Use show more/less functionality with initial count of 4 orders - call before any returns
  const {
    visibleItems: visibleOrders,
    hasMore,
    isExpanded,
    toggleExpanded,
  } = useShowMoreLess(ordersContext?.orders || [], 4);

  if (!ordersContext) {
    return (
      <Flex dir="column" gap="lg" w="full">
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          {headerText}
        </Text>
        <NotFound
          description="Please try refreshing the page"
          icon={PackageIcon}
          text="Orders are not available"
        />
      </Flex>
    );
  }

  const { orders, isLoading, error } = ordersContext;

  if (error) {
    return (
      <Flex dir="column" gap="lg" w="full">
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          {headerText}
        </Text>
        <NotFound
          description="Please try again later"
          icon={PackageIcon}
          text="Unable to load orders"
        />
      </Flex>
    );
  }

  if (isLoading) {
    return (
      <Flex dir="column" gap="lg" w="full">
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          {headerText}
        </Text>
        <Flex align="center" justify="center" py="md" w="full">
          <RedoSpinner size="md" />
        </Flex>
      </Flex>
    );
  }

  if (!orders?.length) {
    return (
      <Flex dir="column" gap="lg" w="full">
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          {headerText}
        </Text>
        <NotFound
          description="We'll update the status of your orders here"
          icon={PackageIcon}
          text="Your most recent orders will show up here"
        />
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="lg" w="full">
      <Text fontSize="md" fontWeight="semibold" textColor="primary">
        {headerText}
      </Text>
      <Flex dir="column" gap="md" w="full">
        {visibleOrders.map((order) => (
          <RedoOrderSummary
            className={styles.orderSummary}
            financialStatus={order.shopify.financial_status}
            imageUrl={
              order.shopify.line_items.find(
                (item) =>
                  !isCoverageFeeProduct({
                    product: { vendor: item.vendor, sku: item.sku },
                  }),
              )?.image?.src
            }
            itemCount={
              order.shopify.line_items.filter(
                (item) => item.current_quantity > 0,
              ).length
            }
            key={order.shopify_id}
            onClick={() => {
              navigate({ to: WidgetView.ORDERS, orderId: order.shopify.name });
            }}
            orderDate={new Date(order.shopify.created_at).toLocaleDateString()}
            orderNumber={order.shopify.name}
            totalPrice={
              order.shopify.current_total_price_set.presentment_money.amount
            }
            w="full"
          />
        ))}
      </Flex>

      <ShowMoreLessButton
        className={styles.showMoreLessButton}
        hasMore={hasMore}
        isExpanded={isExpanded}
        onToggle={toggleExpanded}
        totalCount={orders?.length || 0}
        visibleCount={visibleOrders.length}
      />
    </Flex>
  );
});
